/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.dev/license
 */

import type {StandardSchemaV1} from '@standard-schema/spec';
import {FieldTree} from '../../types';

/**
 * Options used to create a `ValidationError`.
 */
interface ValidationErrorOptions {
  /** Human readable error message. */
  message?: string;
}

/**
 * A type that requires the given type `T` to have a `field` property.
 * @template T The type to add a `field` to.
 *
 * @experimental 21.0.0
 */
export type WithField<T> = T & {fieldTree: FieldTree<unknown>};

/**
 * A type that allows the given type `T` to optionally have a `field` property.
 * @template T The type to optionally add a `field` to.
 *
 * @experimental 21.0.0
 */
export type WithOptionalField<T> = Omit<T, 'fieldTree'> & {fieldTree?: FieldTree<unknown>};

/**
 * A type that ensures the given type `T` does not have a `field` property.
 * @template T The type to remove the `field` from.
 *
 * @experimental 21.0.0
 */
export type WithoutField<T> = T & {fieldTree: never};

/**
 * Create a required error associated with the target field
 * @param options The validation error options
 *
 * @experimental 21.0.0
 */
export function requiredError(options: WithField<ValidationErrorOptions>): RequiredValidationError;
/**
 * Create a required error
 * @param options The optional validation error options
 *
 * @category validation
 * @experimental 21.0.0
 */
export function requiredError(
  options?: ValidationErrorOptions,
): WithoutField<RequiredValidationError>;
export function requiredError(
  options?: ValidationErrorOptions,
): WithOptionalField<RequiredValidationError> {
  return new RequiredValidationError(options);
}

/**
 * Create a min value error associated with the target field
 * @param min The min value constraint
 * @param options The validation error options
 *
 * @category validation
 * @experimental 21.0.0
 */
export function minError(
  min: number,
  options: WithField<ValidationErrorOptions>,
): MinValidationError;
/**
 * Create a min value error
 * @param min The min value constraint
 * @param options The optional validation error options
 *
 * @category validation
 * @experimental 21.0.0
 */
export function minError(
  min: number,
  options?: ValidationErrorOptions,
): WithoutField<MinValidationError>;
export function minError(
  min: number,
  options?: ValidationErrorOptions,
): WithOptionalField<MinValidationError> {
  return new MinValidationError(min, options);
}

/**
 * Create a max value error associated with the target field
 * @param max The max value constraint
 * @param options The validation error options
 *
 * @category validation
 * @experimental 21.0.0
 */
export function maxError(
  max: number,
  options: WithField<ValidationErrorOptions>,
): MaxValidationError;
/**
 * Create a max value error
 * @param max The max value constraint
 * @param options The optional validation error options
 *
 * @category validation
 * @experimental 21.0.0
 */
export function maxError(
  max: number,
  options?: ValidationErrorOptions,
): WithoutField<MaxValidationError>;
export function maxError(
  max: number,
  options?: ValidationErrorOptions,
): WithOptionalField<MaxValidationError> {
  return new MaxValidationError(max, options);
}

/**
 * Create a minLength error associated with the target field
 * @param minLength The minLength constraint
 * @param options The validation error options
 *
 * @category validation
 * @experimental 21.0.0
 */
export function minLengthError(
  minLength: number,
  options: WithField<ValidationErrorOptions>,
): MinLengthValidationError;
/**
 * Create a minLength error
 * @param minLength The minLength constraint
 * @param options The optional validation error options
 *
 * @category validation
 * @experimental 21.0.0
 */
export function minLengthError(
  minLength: number,
  options?: ValidationErrorOptions,
): WithoutField<MinLengthValidationError>;
export function minLengthError(
  minLength: number,
  options?: ValidationErrorOptions,
): WithOptionalField<MinLengthValidationError> {
  return new MinLengthValidationError(minLength, options);
}

/**
 * Create a maxLength error associated with the target field
 * @param maxLength The maxLength constraint
 * @param options The validation error options
 *
 * @category validation
 * @experimental 21.0.0
 */
export function maxLengthError(
  maxLength: number,
  options: WithField<ValidationErrorOptions>,
): MaxLengthValidationError;
/**
 * Create a maxLength error
 * @param maxLength The maxLength constraint
 * @param options The optional validation error options
 *
 * @category validation
 * @experimental 21.0.0
 */
export function maxLengthError(
  maxLength: number,
  options?: ValidationErrorOptions,
): WithoutField<MaxLengthValidationError>;
export function maxLengthError(
  maxLength: number,
  options?: ValidationErrorOptions,
): WithOptionalField<MaxLengthValidationError> {
  return new MaxLengthValidationError(maxLength, options);
}

/**
 * Create a pattern matching error associated with the target field
 * @param pattern The violated pattern
 * @param options The validation error options
 *
 * @category validation
 * @experimental 21.0.0
 */
export function patternError(
  pattern: RegExp,
  options: WithField<ValidationErrorOptions>,
): PatternValidationError;
/**
 * Create a pattern matching error
 * @param pattern The violated pattern
 * @param options The optional validation error options
 *
 * @category validation
 * @experimental 21.0.0
 */
export function patternError(
  pattern: RegExp,
  options?: ValidationErrorOptions,
): WithoutField<PatternValidationError>;
export function patternError(
  pattern: RegExp,
  options?: ValidationErrorOptions,
): WithOptionalField<PatternValidationError> {
  return new PatternValidationError(pattern, options);
}

/**
 * Create an email format error associated with the target field
 * @param options The validation error options
 *
 * @category validation
 * @experimental 21.0.0
 */
export function emailError(options: WithField<ValidationErrorOptions>): EmailValidationError;
/**
 * Create an email format error
 * @param options The optional validation error options
 *
 * @category validation
 * @experimental 21.0.0
 */
export function emailError(options?: ValidationErrorOptions): WithoutField<EmailValidationError>;
export function emailError(
  options?: ValidationErrorOptions,
): WithOptionalField<EmailValidationError> {
  return new EmailValidationError(options);
}

/**
 * Create a standard schema issue error associated with the target field
 * @param issue The standard schema issue
 * @param options The validation error options
 *
 * @category validation
 * @experimental 21.0.0
 */
export function standardSchemaError(
  issue: StandardSchemaV1.Issue,
  options: WithField<ValidationErrorOptions>,
): StandardSchemaValidationError;
/**
 * Create a standard schema issue error
 * @param issue The standard schema issue
 * @param options The optional validation error options
 *
 * @category validation
 * @experimental 21.0.0
 */
export function standardSchemaError(
  issue: StandardSchemaV1.Issue,
  options?: ValidationErrorOptions,
): WithoutField<StandardSchemaValidationError>;
export function standardSchemaError(
  issue: StandardSchemaV1.Issue,
  options?: ValidationErrorOptions,
): WithOptionalField<StandardSchemaValidationError> {
  return new StandardSchemaValidationError(issue, options);
}

/**
 * Common interface for all validation errors.
 *
 * This can be returned from validators.
 *
 * It's also used by the creation functions to create an instance
 * (e.g. `requiredError`, `minError`, etc.).
 *
 * @see [Signal Form Validation](guide/forms/signals/validation)
 * @see [Signal Form Validation Errors](guide/forms/signals/validation#validation-errors)
 * @category validation
 * @experimental 21.0.0
 */
export interface ValidationError {
  /** Identifies the kind of error. */
  readonly kind: string;
  /** Human readable error message. */
  readonly message?: string;
}

export declare namespace ValidationError {
  /**
   * Validation error with a field.
   *
   * This is returned from field state, e.g., catField.errors() would be of a list of errors with
   * `field: catField` bound to state.
   */
  export interface WithField extends ValidationError {
    /** The field associated with this error. */
    readonly fieldTree: FieldTree<unknown>;
  }

  /**
   * Validation error with optional field.
   *
   * This is generally used in places where the result might have a field.
   * e.g., as a result of a `validateTree`, or when handling form submission.
   */
  export interface WithOptionalField extends ValidationError {
    /** The field associated with this error. */
    readonly fieldTree?: FieldTree<unknown>;
  }

  /**
   * Validation error with no field.
   *
   * This is used to strongly enforce that fields are not allowed in validation result.
   */
  export interface WithoutField extends ValidationError {
    /** The field associated with this error. */
    readonly fieldTree?: never;
  }
}

/**
 * Internal version of `NgValidationError`, we create this separately so we can change its type on
 * the exported version to a type union of the possible sub-classes.
 *
 * @experimental 21.0.0
 */
abstract class _NgValidationError implements ValidationError {
  /** Brand the class to avoid Typescript structural matching */
  private __brand = undefined;

  /** Identifies the kind of error. */
  readonly kind: string = '';

  /** The field associated with this error. */
  readonly fieldTree!: FieldTree<unknown>;

  /** Human readable error message. */
  readonly message?: string;

  constructor(options?: ValidationErrorOptions) {
    if (options) {
      Object.assign(this, options);
    }
  }
}

/**
 * An error used to indicate that a required field is empty.
 *
 * @category validation
 * @experimental 21.0.0
 */
export class RequiredValidationError extends _NgValidationError {
  override readonly kind = 'required';
}

/**
 * An error used to indicate that a value is lower than the minimum allowed.
 *
 * @category validation
 * @experimental 21.0.0
 */
export class MinValidationError extends _NgValidationError {
  override readonly kind = 'min';

  constructor(
    readonly min: number,
    options?: ValidationErrorOptions,
  ) {
    super(options);
  }
}

/**
 * An error used to indicate that a value is higher than the maximum allowed.
 *
 * @category validation
 * @experimental 21.0.0
 */
export class MaxValidationError extends _NgValidationError {
  override readonly kind = 'max';

  constructor(
    readonly max: number,
    options?: ValidationErrorOptions,
  ) {
    super(options);
  }
}

/**
 * An error used to indicate that a value is shorter than the minimum allowed length.
 *
 * @category validation
 * @experimental 21.0.0
 */
export class MinLengthValidationError extends _NgValidationError {
  override readonly kind = 'minLength';

  constructor(
    readonly minLength: number,
    options?: ValidationErrorOptions,
  ) {
    super(options);
  }
}

/**
 * An error used to indicate that a value is longer than the maximum allowed length.
 *
 * @category validation
 * @experimental 21.0.0
 */
export class MaxLengthValidationError extends _NgValidationError {
  override readonly kind = 'maxLength';

  constructor(
    readonly maxLength: number,
    options?: ValidationErrorOptions,
  ) {
    super(options);
  }
}

/**
 * An error used to indicate that a value does not match the required pattern.
 *
 * @category validation
 * @experimental 21.0.0
 */
export class PatternValidationError extends _NgValidationError {
  override readonly kind = 'pattern';

  constructor(
    readonly pattern: RegExp,
    options?: ValidationErrorOptions,
  ) {
    super(options);
  }
}

/**
 * An error used to indicate that a value is not a valid email.
 *
 * @category validation
 * @experimental 21.0.0
 */
export class EmailValidationError extends _NgValidationError {
  override readonly kind = 'email';
}

/**
 * An error used to indicate an issue validating against a standard schema.
 *
 * @category validation
 * @experimental 21.0.0
 */
export class StandardSchemaValidationError extends _NgValidationError {
  override readonly kind = 'standardSchema';

  constructor(
    readonly issue: StandardSchemaV1.Issue,
    options?: ValidationErrorOptions,
  ) {
    super(options);
  }
}

/**
 * The base class for all built-in, non-custom errors. This class can be used to check if an error
 * is one of the standard kinds, allowing you to switch on the kind to further narrow the type.
 *
 * @example
 * ```ts
 * const f = form(...);
 * for (const e of form().errors()) {
 *   if (e instanceof NgValidationError) {
 *     switch(e.kind) {
 *       case 'required':
 *         console.log('This is required!');
 *         break;
 *       case 'min':
 *         console.log(`Must be at least ${e.min}`);
 *         break;
 *       ...
 *     }
 *   }
 * }
 * ```
 *
 * @category validation
 * @experimental 21.0.0
 */
export const NgValidationError: abstract new () => NgValidationError = _NgValidationError as any;
export type NgValidationError =
  | RequiredValidationError
  | MinValidationError
  | MaxValidationError
  | MinLengthValidationError
  | MaxLengthValidationError
  | PatternValidationError
  | EmailValidationError
  | StandardSchemaValidationError;
